import React, { useState } from 'react';

import { api } from 'common';
import { AppointmentActionButton } from './AppointmentActionButton';
import { Button } from '../../components/Button';
import { DoctorAppointmentProfileView } from './DoctorAppointmentProfileView';
import { isAppointmentCancellableNow } from '../../utils/confirmCancel';

import styles from './index.module.css';
import { useTranslation } from 'react-i18next';

interface Props {
  appointment: api.NotificationIntent.Appointment;
  pending?: boolean;
  getAppointments: () => Promise<void>;
  setLoadingText: (val: React.ReactNode) => void;
  onAppointmentsReply: (
    appointments: api.NotificationIntent.Appointment[],
    reply: api.EhrTimeSlot.AppointmentStatus,
  ) => void;
}

export const AppointmentsListDoctorView: React.FC<Props> = ({
  appointment,
  pending,
  getAppointments,
  setLoadingText,
  onAppointmentsReply,
}) => {
  const { t } = useTranslation();
  const [actionModalOpen, setActionModalOpen] = useState(false);
  const isReschedulable =
    appointment.rescheduleAppointmentKind &&
    isAppointmentCancellableNow(appointment);
  const rescheduleButton = isReschedulable ? (
    <Button
      type="button"
      onClick={() => setActionModalOpen(true)}
      children={
        appointment.isCancellable
          ? t('Reschedule or cancel appointment')
          : t('Reschedule appointment')
      }
      invertColors={true}
      className={styles.ConfirmCancelButton}
    />
  ) : undefined;

  return (
    <DoctorAppointmentProfileView
      key={appointment.encounter.id}
      id={appointment.encounter.id}
      startTime={appointment.encounter.startTime}
      arrivalTime={appointment.encounter.arrivalTime || undefined}
      status={appointment.encounter.status || undefined}
      provider={appointment.provider}
      practice={appointment.practice}
      virtualVisit={appointment.virtualVisit}
      hideEncounterStartTimeByVisitKind={
        appointment.preferences.hideEncounterStartTimeByVisitKind
      }
      kindOfVisit={appointment.encounter.kindOfVisit || undefined}
      extraInfoContent={
        isReschedulable ? (
          <AppointmentActionButton
            appointment={appointment}
            encounterId={appointment.encounter.id}
            actionModalOpen={actionModalOpen}
            setActionModalOpen={setActionModalOpen}
            className={styles.ConfirmCancelButton}
            onCancelAppointment={() => {
              setLoadingText(t('Cancelling appointment...'));
              onAppointmentsReply(
                [appointment],
                api.EhrTimeSlot.AppointmentStatus.Cancelled,
              );
            }}
            onRescheduleAppointment={() => {
              setLoadingText(t('Rescheduling appointment...'));
              onAppointmentsReply(
                [appointment],
                api.EhrTimeSlot.AppointmentStatus.Rescheduled,
              );
            }}
            getAppointments={getAppointments}
            pending={pending}
            cancelOnly={!appointment.rescheduleAppointmentKind}
            practicePhoneNumber={appointment.practice.phoneNumber}
            timezone={appointment.practice.timezone}
          />
        ) : undefined
      }
      actionInfoContext={rescheduleButton}
    />
  );
};
